import { Command, ButtonView, toWidgetEditable } from "ckeditor5";
import shortid from "shortid";
import { css } from "styled-components";

import { IconTextSelectableLine } from "@examspark/common-ui/dist/components/icons";

import ItemEditor from "./ItemEditor";
import { PluginWithStyles } from "./PluginWithStyles";

export const UI_BUTTON_HOTTEXT = "hottext";
export const COMMAND_INSERT_HOTTEXT = "insertHottext";

export class InsertHotTextCommand extends Command {
  value = false;

  refresh() {
    const model = this.editor.model;
    const context = model.document.selection.focus?.parent;
    const range = model.document.selection.getFirstRange();

    const items = Array.from(range?.getItems() ?? []);

    this.value = context?.name === "hottext";

    const isEnabledForDeselection = this.value && items.length === 0;

    const isEnabledForSelection =
      !this.value &&
      !!context?.is("element") &&
      model.schema.checkChild(context, "hottext") &&
      items.every((i) => i.is("$text") || i.is("$textProxy")) &&
      items.length > 0;

    this.isEnabled = isEnabledForDeselection || isEnabledForSelection;
  }

  execute() {
    const range = this.editor.model.document.selection.getFirstRange();
    if (range == null) return;

    // if we're already in a hottext, unwrap (delete) it
    const context = this.editor.model.document.selection.focus?.parent;
    if (context?.name === "hottext") {
      this.editor.model.change((writer) => {
        writer.unwrap(context);
      });
    } else {
      this.editor.model.change((writer) => {
        writer.wrap(
          range,
          writer.createElement("hottext", {
            id: shortid(),
          }),
        );
      });
    }
  }
}

export default class ItemEditorHotText extends PluginWithStyles {
  public static get requires() {
    return [ItemEditor];
  }

  public static buildEditorStyles = () => css`
    .hottext {
      background-color: #e2e2e2;
      padding: 0.2rem 0.2rem;
      border-radius: 0.2rem;
    }
  `;

  init() {
    this.editor.model.schema.register("hottext", {
      allowAttributes: ["id"],
      allowWhere: "$text",
      allowChildren: "$text",
      isInline: true,
      isContent: true,
      isObject: true,
    });

    this.editor.conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        classes: "hottext",
      },
      model: (e, { writer, consumable }) => {
        consumable.consume(e, {
          name: true,
          attributes: "data-hottext-id",
          classes: "hottext",
        });
        return writer.createElement("hottext", {
          id: e.getAttribute("data-hottext-id"),
        });
      },
    });

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "hottext",
      view: (e, { writer }) =>
        toWidgetEditable(
          writer.createEditableElement("span", {
            class: "hottext",
            "data-hottext-id": e.getAttribute("id"),
          }),
          writer,
        ),
    });

    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "hottext",
      view: (e, { writer }) =>
        writer.createContainerElement("span", {
          class: "hottext",
          "data-hottext-id": e.getAttribute("id"),
        }),
    });

    const command = new InsertHotTextCommand(this.editor);
    this.editor.commands.add(COMMAND_INSERT_HOTTEXT, command);

    this.editor.ui.componentFactory.add(UI_BUTTON_HOTTEXT, (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: this.editor.t("Insert Selectable Text"),
        icon: IconTextSelectableLine.Content,
        tooltip: true,
      });
      button.bind("isOn", "isEnabled").to(command, "value", "isEnabled");
      button.on("execute", () => command.execute());
      return button;
    });
  }
}
